/**
 * Color Picker — Core + Workshop Palette
 *
 * Custom element <color-picker-workshop> that:
 *   - Manages expand/collapse of the Workshop Palette
 *   - Handles unified swatch selection across core + workshop rows
 *   - Fires native change events on the hidden radio inputs so Shopify's
 *     VariantSelects web component picks up the selection and triggers
 *     the standard variant-change flow (price update, media swap, etc.)
 *   - Persists workshop open state across Shopify section re-renders
 *   - Replays the staggered animation each time the palette opens manually
 */

if (!customElements.get('color-picker-workshop')) {
  class ColorPickerWorkshop extends HTMLElement {
    constructor() {
      super();
      this.cta = null;
      this.wrap = null;
      this.nameDisplay = null;
    }

    connectedCallback() {
      this.cta = this.querySelector('[data-cta]');
      this.wrap = this.querySelector('[data-workshop-wrap]');
      this.nameDisplay = this.querySelector('[data-selected-name]');

      if (this.cta && this.wrap) {
        this.cta.addEventListener('click', this.toggle.bind(this));
      }

      // Listen for clicks on all swatch buttons (core + workshop)
      this.addEventListener('click', this.handleSwatchClick.bind(this));

      // Restore workshop palette open state after Shopify re-renders the section.
      // When a variant changes, Shopify fetches fresh HTML and swaps the
      // variant-selects node (via HTMLUpdateUtility.viewTransition). This
      // destroys the old DOM and creates a new one, losing the open state.
      // We persist the state on the <product-info> element so the new
      // custom element can read it on connectedCallback.
      this.restoreOpenState();
    }

    /**
     * Check whether the palette was open before a re-render and restore it.
     * Uses a data attribute on the closest <product-info> ancestor as
     * persistent storage (it survives the variant-selects swap).
     */
    restoreOpenState() {
      const productInfo = this.closest('product-info');
      if (productInfo && productInfo.dataset.workshopOpen === 'true') {
        // Open instantly without animation
        if (this.wrap) {
          this.wrap.classList.add('is-open', 'no-animate');
        }
        if (this.cta) {
          this.cta.classList.add('is-open');
          const openText = this.cta.dataset.textOpen;
          if (openText) {
            const textEl = this.cta.querySelector('[data-cta-text]');
            if (textEl) textEl.textContent = openText;
          }
        }
      }
    }

    /**
     * Save the open/closed state to the product-info element so it
     * persists across section re-renders.
     */
    persistOpenState(isOpen) {
      const productInfo = this.closest('product-info');
      if (productInfo) {
        productInfo.dataset.workshopOpen = isOpen ? 'true' : 'false';
      }
    }

    toggle() {
      // If the panel was restored without animation, remove the no-animate
      // flag so future opens get the staggered reveal
      if (this.wrap.classList.contains('no-animate')) {
        this.wrap.classList.remove('no-animate');
      }

      const isOpen = this.wrap.classList.toggle('is-open');
      this.cta.classList.toggle('is-open', isOpen);
      this.persistOpenState(isOpen);

      // Update CTA text
      const openText = this.cta.dataset.textOpen;
      const closedText = this.cta.dataset.textClosed;
      this.cta.querySelector('[data-cta-text]').textContent = isOpen ? openText : closedText;

      // Replay staggered animation
      if (isOpen) {
        const swatches = this.wrap.querySelectorAll('.color-picker__named-swatch');
        swatches.forEach((s) => {
          s.style.animation = 'none';
          void s.offsetHeight;
          s.style.animation = '';
        });
      }
    }

    handleSwatchClick(e) {
      const btn = e.target.closest('[data-color-swatch]');
      if (!btn) return;

      const colorName = btn.dataset.colorName;
      const radioId = btn.dataset.radioTarget;
      if (!colorName || !radioId) return;

      // Clear all selected states in this picker
      this.querySelectorAll('.is-selected').forEach((el) => el.classList.remove('is-selected'));
      btn.classList.add('is-selected');

      // Update displayed color name
      if (this.nameDisplay) {
        this.nameDisplay.textContent = colorName;
      }

      // Persist open state before triggering the variant change,
      // because the change will cause a section re-render
      if (this.wrap && this.wrap.classList.contains('is-open')) {
        this.persistOpenState(true);
      }

      // Check the corresponding hidden radio input and fire a change event
      // so Shopify's VariantSelects picks it up
      const radio = this.querySelector('#' + CSS.escape(radioId));
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  customElements.define('color-picker-workshop', ColorPickerWorkshop);
}
